import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AlertManager } from '@agilemd/ui-alert';
import { Button } from '@agilemd/ui-button-legacy';
import { ErrorBoundary } from '@agilemd/ui-error-boundary';
import { Icon } from '@agilemd/ui-icon';
import { LoadingIndicator } from '@agilemd/ui-loading-indicator';
import { ProgressIndicator } from '@agilemd/ui-progress-indicator';
import { SnackbarManager } from '@agilemd/ui-snackbar';
import { Spacer } from '@agilemd/ui-spacer';
import { Tabs } from '@agilemd/ui-tabs-legacy';
import { addVariants } from '@agilemd/ui-utils';

import { reset } from '../../../data/actions.js';
import { getProcedureCptCode } from '../../../data/procedureSlice.js';
import { ICONS } from '../../../utils/fontawesome.js';
import { TooltipContext } from '../../../views/components/Tooltip/index.js';
import { TABS } from '../../../views/constants.js';
import { useSurpasResponse } from '../../../views/hooks/usePatientEventRate.js';
import styles from './styles.module.scss';
import useTooltips from './useTooltips.js';

const TAB_COMPONENTS = {
  [TABS.PROCEDURE]: React.lazy(() => import('../../../views/pages/Procedures/index.js')),
  [TABS.RESULTS]: React.lazy(() => import('../../../views/pages/Results/index.js')),
  [TABS.USER_GUIDE]: React.lazy(() => import('../../../views/pages/UserGuide/index.js')),
  [TABS.ABOUT]: React.lazy(() => import('../../../views/pages/About/index.js')),
  [TABS.DOCUMENTATION]: React.lazy(() => import('../../../views/pages/Documentation/index.js')),
  [TABS.PATIENT]: React.lazy(() => import('../../../views/pages/PatientPrintout/index.js'))
};

function SaveResponse() {
  const { isSaving, isValid, saveResponse } = useSurpasResponse();

  if (!isValid) {
    return null;
  }

  return (
    <>
      <Button
        variant="small"
        color="green"
        onClick={saveResponse}
        className={styles['surpas-save']}
      >
        <Spacer right={1} isInline>
          <Icon icon={ICONS.SAVE} />
        </Spacer>
        Save to chart
      </Button>
      {isSaving && <ProgressIndicator />}
    </>
  );
}

function Shell({ offsets: outerOffsets = {} }) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(TABS.PROCEDURE);

  const { tooltipsEl, updateTooltip, nodeRef } = useTooltips({
    offsets: outerOffsets
  });

  const isProcedureSelected = useSelector(getProcedureCptCode);
  // Automatically select the result tab if a procedure is selected
  useEffect(() => {
    if (isProcedureSelected) {
      setTab(TABS.RESULTS);
    }
  }, [isProcedureSelected]);

  return (
    <TooltipContext.Provider value={updateTooltip}>
      <div ref={nodeRef} className="surpas">
        <ErrorBoundary>
          <AlertManager>
            <SnackbarManager>
              {tooltipsEl}
              <Tabs onChange={setTab} currentTab={tab}>
                <Tabs.List variant="white" className={styles['surpas-tabs']}>
                  <Spacer isInlineBlock left={5} />
                  <Tabs.Item value={TABS.PROCEDURE}>Procedure</Tabs.Item>
                  <Tabs.Item value={TABS.RESULTS} isDisabled={!isProcedureSelected}>
                    Results
                  </Tabs.Item>
                  <Tabs.Item value={TABS.DOCUMENTATION} isDisabled={!isProcedureSelected}>
                    Documentation
                  </Tabs.Item>
                  <Tabs.Item value={TABS.PATIENT} isDisabled={!isProcedureSelected}>
                    Patient handout
                  </Tabs.Item>
                  <Tabs.Item value={TABS.USER_GUIDE}>User guide</Tabs.Item>
                  <Tabs.Item value={TABS.ABOUT}>About</Tabs.Item>
                  {isProcedureSelected && (
                    <Button
                      variant="small"
                      onClick={() => {
                        setTab(TABS.PROCEDURE);
                        dispatch(reset());
                      }}
                      className={styles['surpas-reset']}
                    >
                      Restart
                    </Button>
                  )}
                  <SaveResponse />
                </Tabs.List>
                {Object.entries(TAB_COMPONENTS).map(([tabOfComponent, Component]) => (
                  <Tabs.Pane
                    key={tabOfComponent}
                    value={tabOfComponent}
                    className={addVariants('surpas-page', styles)}
                  >
                    {() => (
                      <React.Suspense fallback={<LoadingIndicator />}>
                        <Component />
                      </React.Suspense>
                    )}
                  </Tabs.Pane>
                ))}
              </Tabs>
            </SnackbarManager>
          </AlertManager>
        </ErrorBoundary>
      </div>
    </TooltipContext.Provider>
  );
}
Shell.propTypes = {
  offsets: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired
  })
};
export default Shell;
